/** @format */

import { axiosInstance } from '@common/application/context/Providers';
import { DocumentClient } from '@common/model/apiClient';
import { ReturnedAccount } from '@common/model/apiClient/Account';
import { Company } from '@common/model/apiClient/Company';
import { ApiResponse, AxiosApiResponse } from '@common/types/api';
import { ElasticResponse } from '@common/types/elastic';
import { Nullable } from '@common/types/utils';

export class User {
  entraId: string;
  trackupId: string;
  email: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: Nullable<string>;
  defaultCompanyId: Nullable<number>;
  companies?: Array<Company>;

  constructor(entraId: string, trackupId: string, email: string) {
    this.entraId = entraId;
    this.trackupId = trackupId;
    this.email = email;
  }

  static list(search: string, page: number): ElasticResponse<User> {
    // Ensure this code is not ran by SSR!
    if (typeof window !== 'undefined') {
      return axiosInstance
        .get(`/api/v1/users`, {
          params: {
            search,
            page,
          },
        })
        .then((res) => ({ ...res.data }));
    }
    throw new Error();
  }

  static get(id: string): AxiosApiResponse<User> {
    return axiosInstance.get(`/api/v1/users/${id}`);
  }

  static getCompanies(id: string): ApiResponse<{ company: Company }[]> {
    return axiosInstance.get(`/api/v2/users/${id}/companies`).then((res) => ({ ...res.data }));
  }

  static delete(id: string): Promise<Record<string, unknown>> {
    return axiosInstance.delete(`/api/v1/users/${id}`).then((res) => ({ ...res.data }));
  }

  static undelete(id: string): Promise<Record<string, unknown>> {
    return axiosInstance.post(`/api/v1/users/${id}/undelete`).then((res) => ({ ...res.data }));
  }

  static update(representation: Record<string, any>, id: string): Promise<Record<string, unknown>> {
    return axiosInstance.patch(`/api/v1/users/${id}`, { payload: representation }).then((res) => ({ ...res.data }));
    // .catch((err) => console.log(err.response));
  }

  static getStats(
    id: string,
    timeSpan: { start: Date; end: Date },
  ): ApiResponse<{
    savingsTotal: number;
    docCount: number;
  }> {
    return axiosInstance.get(`/api/v1/users/stats/${id}`, { params: { start: timeSpan.start, end: timeSpan.end } }).then((res) => ({ ...res.data }));
  }

  add(): Promise<Record<string, unknown>> {
    return axiosInstance
      .post(`/api/v1/users`, {
        payload: {
          entraId: this.entraId,
          trackupId: this.trackupId,
          email: this.email,
        },
      })
      .then((res) => ({ ...res.data }));
  }

  static addFavouriteAccount(userId: string, accountId: number) {
    return axiosInstance.post(`/api/v1/users/${userId}/favourite-accounts`, { payload: { accountId } });
  }

  static deleteFavouriteAccount(userId: string, accountId: number) {
    return axiosInstance.delete(`/api/v1/users/${userId}/favourite-accounts`, { params: { accountId } });
  }

  static getFavouriteAccounts(userId: string): AxiosApiResponse<ReturnedAccount[]> {
    return axiosInstance.get(`/api/v1/users/${userId}/favourite-accounts`);
  }

  static getRecentDocuments(userId: string): AxiosApiResponse<Array<DocumentClient>> {
    return axiosInstance.get(`/api/v1/users/${userId}/recent-documents`);
  }

  static getHistory(userId: string) {
    return axiosInstance.get(`/api/v1/users/${userId}/history`);
  }

  static addToHistory(userId: string, newItem: { accountId?: number; documentId?: number }) {
    return axiosInstance.post(`/api/v1/users/${userId}/history`, { payload: newItem });
  }

  static getSettings(userId: string) {
    return axiosInstance.get(`/api/v1/users/settings/${userId}`);
  }

  static updateSettings(userId: string, newSettings: {}) {
    return axiosInstance.patch(`/api/v1/users/settings/${userId}`, { payload: newSettings });
  }

  static saveLegacySettings(userId: string) {
    return axiosInstance.patch(`/api/v1/users/${userId}/legacy-settings`, {});
  }
}
